import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
} from 'react';
import { z } from 'zod';

import { codec, useQueryParams } from '@/hooks/router/use-query-params';

import { AddWorkstreamModal } from './add-workstream.modal';

export type AddWorkstreamModalOpen = 'custom' | 'template' | undefined;

const AddWorkstreamModalContext = createContext<
  (open: AddWorkstreamModalOpen) => void
>(() => {
  // This is a no-op function used as the default value for the context
  // It will be overridden by the actual provider
});

export function useAddWorkstreamModal() {
  return useContext(AddWorkstreamModalContext);
}

export function AddWorkstreamModalProvider({
  children,
}: PropsWithChildren<object>) {
  const [{ formType }, setParams] = useQueryParams({
    formType: {
      key: 'add_workstream',
      schema: z.enum(['custom', 'template']).optional(),
      codec: codec.string,
    },
  });

  const setFormType = useCallback(
    function (formType: AddWorkstreamModalOpen) {
      setParams.replace({
        formType,
      });
    },
    [setParams],
  );

  return (
    <AddWorkstreamModalContext.Provider value={setFormType}>
      {children}
      <AddWorkstreamModal
        formType={formType}
        onClose={() => setFormType(undefined)}
      />
    </AddWorkstreamModalContext.Provider>
  );
}
